import { memo, useRef } from 'react';

import { HERO_VIDEO, PAGE_VIDEO } from '@config/backdrop';
import { useInView } from '@hooks/useInView';

import styles from './HomeBackdrop.module.scss';
import { LoopVideo } from './LoopVideo';

/**
 * The page video starts once the hero no longer reaches the bottom 40% of
 * the window: just before its fade-in begins (see the stylesheet), so it is
 * already moving when it appears.
 */
const PAGE_VIDEO_START_MARGIN = '-60% 0px 0px 0px';

/**
 * The home page's moving background: two videos in sequence.
 *
 * Behind the hero, a data-streaked globe, the visual version of what the
 * orbit says in words. As the hero leaves, it fades out and a second video
 * fades in, which then stays behind the rest of the page. Both hold still
 * while the page scrolls over them (HomeBackdrop.module.scss).
 *
 * Rendered inside the hero, whose scroll timeline drives both fades. The
 * outer box covers the hero's area, so it also tells the videos when the
 * hero is on screen; the videos themselves are pinned to the window and
 * would never report leaving it.
 *
 * Both play under reduced motion, as the orbit does, at the owner's
 * request.
 */
export const HomeBackdrop = memo(() => {
  const heroAreaRef = useRef<HTMLDivElement>(null);
  const heroOnScreen = useInView(heroAreaRef);
  const heroReachesBottom = useInView(heroAreaRef, PAGE_VIDEO_START_MARGIN);

  return (
    <div ref={heroAreaRef} className={styles.backdrop} aria-hidden="true">
      <div className={styles.page}>
        <LoopVideo video={PAGE_VIDEO} playing={!heroReachesBottom} />
      </div>
      <div className={styles.hero}>
        <LoopVideo video={HERO_VIDEO} playing={heroOnScreen} />
      </div>
    </div>
  );
});

HomeBackdrop.displayName = 'HomeBackdrop';
